import { useBatchPayments } from "Api/Queries/Banking/useBatchPayments";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { BatchPaymentListContent } from "Components/BatchPayments/BatchPaymentListContent";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { Resources, useResource } from "Translations/Resources";
import { useMemo, type ReactNode } from "react";
import { Stack } from "@mui/material";

const PAGE_RESOURCES = Resources.Pages.Titles;
const BP_RESOURCES = Resources.Banking.BatchPayments;

export const BatchPaymentListPage = () => {
  const { t } = useResource();
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useBatchPayments();

  const items = useMemo(
    () => data?.pages?.flatMap(({ items }) => items) ?? [],
    [data],
  );

  const totalCount = data?.pages?.[0]?.totalCount ?? 0;

  if (isLoading) {
    return (
      <Layout>
        <PrimaryPaper sx={{ height: "200px" }}>
          <CenteredCircularProgress />
        </PrimaryPaper>
      </Layout>
    );
  }

  if (isError) {
    return (
      <Layout>
        <PrimaryPaper>
          <ErrorText>{t(BP_RESOURCES.NotFound)}</ErrorText>
        </PrimaryPaper>
      </Layout>
    );
  }

  return (
    <Layout>
      <PrimaryPaper>
        <Stack gap={2}>
          <BatchPaymentListContent items={items} totalCount={totalCount} />
          {hasNextPage && (
            <PrimaryButton
              variant="text"
              color="info"
              onClick={() => fetchNextPage()}
              loading={isFetchingNextPage}
              sx={{ alignSelf: "center" }}
            >
              {t(BP_RESOURCES.LoadMore)}
            </PrimaryButton>
          )}
        </Stack>
      </PrimaryPaper>
    </Layout>
  );
};

export default BatchPaymentListPage;

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout
      title={t(PAGE_RESOURCES.BatchPayments)}
      backNavigation={{
        mode: "historyStack",
        fallbackUrl: null,
      }}
    >
      <StyledPageWrapper>
        <div className="left-content">{children}</div>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};
